import { API_ROUTES } from '@/constants/apiRoutes';
import {
  UpdatePasswordFields,
  UpdateEmailFields,
  UpdatePhoneFields,
  IdempotentRequest,
} from '@/types/common';
import {
  AddCustomerDocumentFields,
  ChangeDepositRulesFormFields,
  CustomerDetails,
  CustomerFormFields,
  CustomerForSelectItem,
  CustomerListResponse,
  DocumentsListResponse,
} from '@/types/customers';
import { api, formatAddressForPost, formatCurrencyForPost } from '@/utils/api';

export const getCustomersListService = async (queryParams: string) => {
  const { data } = await api<CustomerListResponse>({
    url: `${API_ROUTES.CUSTOMERS}?${queryParams}`,
    method: 'get',
    version: 2,
  });

  return data;
};

export const getCustomerDetailsService = async (uuid: string) => {
  const { data } = await api<CustomerDetails>({
    url: `${API_ROUTES.CUSTOMERS}${uuid}`,
    method: 'get',
    version: 2,
  });

  return data;
};

export const getCustomerListForSelectionService = async (queryParams = '') => {
  const { data } = await api<CustomerForSelectItem[]>({
    url: `${API_ROUTES.CUSTOMERS}for-selection?${queryParams}`,
    method: 'get',
    version: 2,
  });

  return data;
};

export const getCustomerDocumentsService = async (uuid: string) => {
  const { data } = await api<DocumentsListResponse>({
    url: `${API_ROUTES.CUSTOMERS}documents?consumer_id=${uuid}`,
    method: 'get',
    version: 2,
  });

  return data;
};

export const addCustomerDocumentsService = async (
  document: AddCustomerDocumentFields,
) => {
  const { data } = await api({
    url: `${API_ROUTES.CUSTOMERS}documents`,
    method: 'post',
    version: 2,
    data: {
      url: document.url,
      consumer_id: document.customerId,
      side: document.side,
      consumer_document_type: document.documentType,
      consumer_document_subtype: document.documentSubtype,
    },
  });

  return data;
};

export const deleteCustomerDocumentsService = async (uuid: string) => {
  const { data } = await api({
    url: `${API_ROUTES.CUSTOMERS}documents/${uuid}`,
    method: 'delete',
    version: 2,
  });

  return data;
};

export const addCustomerService = async (
  payload: IdempotentRequest<CustomerFormFields>,
) => {
  const currentAddress = formatAddressForPost(payload.currentAddress);

  const { data } = await api<string>({
    url: `${API_ROUTES.CUSTOMERS}`,
    method: 'post',
    version: 2,
    data: {
      first_name: payload.firstName,
      middle_name: payload.middleName,
      last_name: payload.lastName,
      email: payload.email,
      confirm_email: payload.confirmEmail,
      phone_number: payload.phone,
      confirm_phone_number: payload.confirmPhone,
      date_of_birth: payload.dob,
      ssn: payload.ssn,
      confirm_ssn: payload.confirmSsn,
      company_id: payload.company,
      advisor_ids: payload.advisors,
      current_address: currentAddress,
      mailing_address: payload.sameMailingAddress
        ? currentAddress
        : formatAddressForPost(payload.mailingAddress),
      initial_minimum_deposit: formatCurrencyForPost(payload.min),
    },
    idempotencyKey: payload.idempotencyKey,
  });

  return data;
};

export const editCustomerService = async (
  payload: IdempotentRequest<CustomerFormFields>,
) => {
  const { currentAddress, mailingAddress, sameMailingAddress, uuid, advisors } =
    payload;
  const mappedCurrentAddress = formatAddressForPost(currentAddress);

  const { data } = await api<string>({
    url: `${API_ROUTES.CUSTOMERS}${uuid}`,
    method: 'put',
    version: 2,
    data: {
      advisor_ids: advisors,
      current_address: mappedCurrentAddress,
      mailing_address: sameMailingAddress
        ? mappedCurrentAddress
        : formatAddressForPost(mailingAddress),
      initial_minimum_deposit: formatCurrencyForPost(payload.min),
    },
    idempotencyKey: payload.idempotencyKey,
  });

  return data;
};

export const deactivateCustomerService = async (
  uuid: string,
  preflight: boolean,
) => {
  const { data } = await api<{ canDeactivate: boolean }>({
    url: `${API_ROUTES.CUSTOMERS}${uuid}/deactivate`,
    method: 'post',
    version: 2,
    data: {
      preflight,
    },
  });

  return data;
};

export const reactivateCustomerService = async (uuid: string) => {
  const { data } = await api({
    url: `${API_ROUTES.CUSTOMERS}${uuid}/reactivate`,
    method: 'post',
    version: 2,
  });

  return data;
};

interface ResetCustomerPasswordProps extends UpdatePasswordFields {
  uuid: string;
}

export const resetCustomerPasswordService = async (
  payload: ResetCustomerPasswordProps,
) => {
  const { data } = await api({
    url: `${API_ROUTES.CUSTOMERS}${payload.uuid}/reset-password`,
    method: 'post',
    version: 2,
    data: {
      new_password: payload.password,
      repeat_new_password: payload.confirmPassword,
    },
  });

  return data;
};

export const startCustomerUpdateService = async (uuid: string) => {
  const { data } = await api({
    url: `${API_ROUTES.CUSTOMERS}${uuid}/start-auth-update`,
    method: 'post',
    version: 2,
  });

  return data;
};

export const changeCustomerEmailService = async (
  payload: UpdateEmailFields,
) => {
  const { data } = await api({
    url: `${API_ROUTES.CUSTOMERS}${payload.uuid}/update-email`,
    method: 'post',
    version: 2,
    data: {
      otp_code: payload.otp,
      new_email: payload.email,
      repeat_new_email: payload.confirmEmail,
    },
  });

  return data;
};

export const changeCustomerPhoneService = async (
  payload: UpdatePhoneFields,
) => {
  const { data } = await api({
    url: `${API_ROUTES.CUSTOMERS}${payload.uuid}/update-phone`,
    method: 'post',
    version: 2,
    data: {
      otp_code: payload.otp,
      new_phone_number: payload.phone,
      repeat_new_phone_number: payload.confirmPhone,
    },
  });

  return data;
};

export const changeCustomerDepositRulesService = async (
  payload: ChangeDepositRulesFormFields,
) => {
  const { data } = await api({
    url: `${API_ROUTES.CUSTOMERS}${payload.uuid}/initial-deposit`,
    method: 'post',
    version: 2,
    data: {
      minimum_init_deposit: formatCurrencyForPost(payload.min),
      maximum_init_deposit: formatCurrencyForPost(payload.max),
    },
  });

  return data;
};
